// Crew editor (George 2026-07-30: "everyone manages crew in the SAME
// location"). The ops-facing window onto the estimate's Personnel slots,
// rendered on the Operations → Allocations drill-in for any job with a
// current estimate. Names/craft/shift live HERE (and on Estimating →
// Personnel — same rows, same save path via savePersonnelCore) and forward-
// sync onto job_allocations; allocation DATES / PD / travel / verifications
// stay ops-owned on the assignment rows below. The server payload is
// cost-walled (utils/crewEditorShape.js) — no rate ever reaches this
// component.
//
// buildCrewSlots is the POSITION-model slot derivation, extracted from
// Estimating.jsx's PersonnelPanel so the two surfaces can never disagree on
// which slots exist. Mirrored server-side in estimatePersonnelGantt.js —
// keep the three in lockstep.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles } from './UI';

export const SECTION_ORDER = ['Turnaround', 'Safety', 'CSVM', 'Rescue'];

// POSITION model (George 2026-07-08): a slot is a PERSON, not a shift×HC
// cell. Positions = max concurrent staffing (day_hc + night_hc) across dated
// phases (NOT qty_positions — real data has it out of sync with the phases).
// Position i works Day in a phase while i < day_hc, Night while
// i < day_hc + night_hc, and sits the phase out beyond that — the same
// people rotate shifts across phases. A slot's SHIFT label = the shift it
// works the most days (tie → Day); mixed slots flag the odd phase's shift in
// the schedule text.
export function buildCrewSlots(current) {
  const sectionOf = (l) => {
    const g = (l.labor_group || '').trim(); if (g) return g;
    const c = (l.craft || '').toLowerCase();
    if (/rescue/.test(c)) return 'Rescue';
    if (/confined space|csvm|hole watch/.test(c)) return 'CSVM';
    if (/safety/.test(c)) return 'Safety';
    return 'Turnaround';
  };
  const shiftAt = (ph, i) => {
    const d = Math.max(0, Math.floor(Number(ph.day_hc) || 0));
    const nt = Math.max(0, Math.floor(Number(ph.night_hc) || 0));
    return i < d ? 'Day' : i < d + nt ? 'Night' : null;
  };
  const positionsOf = (l) => {
    let m = 0;
    for (const ph of (l.phases || [])) m = Math.max(m, Math.floor(Number(ph.day_hc) || 0) + Math.floor(Number(ph.night_hc) || 0));
    return m;
  };
  const spanDays = (ph) => {
    const s = new Date(String(ph.start_date).slice(0, 10)), e = new Date(String(ph.end_date).slice(0, 10));
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  };
  // Several base estimates on one job (the ops Crew editor payload carries
  // `estimates`, George 2026-11-02 Valero Oleflex TA + HH): each section is
  // headed by its estimate so the two scopes never read as one list.
  const multi = Array.isArray(current.estimates) && current.estimates.length > 1;
  const slots = [];
  for (const l of (current.labor_lines || [])) {
    if (l.rate_only) continue;
    const count = positionsOf(l);
    for (let i = 0; i < count; i++) {
      const phases = (l.phases || [])
        .filter(ph => ph.start_date && ph.end_date && shiftAt(ph, i))
        .sort((a, b) => String(a.start_date).slice(0, 10).localeCompare(String(b.start_date).slice(0, 10)))
        .map(ph => ({ ...ph, slotShift: shiftAt(ph, i) }));
      let dayD = 0, nightD = 0;
      for (const ph of phases) (ph.slotShift === 'Day' ? dayD += spanDays(ph) : nightD += spanDays(ph));
      const shift = nightD > dayD ? 'Night' : 'Day';
      slots.push({
        key: `${l.id}|${shift}|${i}`, labor_line_id: l.id, craft: l.craft || '', shift,
        rotates: dayD > 0 && nightD > 0,
        line_no: l.line_no, estimate_label: multi ? l.estimate_label : null,
        slot_index: i, section: multi ? `${l.estimate_label} · ${sectionOf(l)}` : sectionOf(l), sub: l.labor_type === 'Subcontractor',
        phases,
      });
    }
  }
  // Row order follows the schedule (George 2026-07-02): earliest phase start
  // first — full-run people (on from Pre-TA) lead, mechanical-only follow,
  // post-only land last. Ties: more phases first, Day before Night, then
  // slot order. Undated slots sink to the bottom.
  const startOf = (s) => (s.phases.length ? String(s.phases[0].start_date).slice(0, 10) : '9999-12-31');
  slots.sort((a, b) =>
    startOf(a).localeCompare(startOf(b))
    || b.phases.length - a.phases.length
    || (a.shift === b.shift ? 0 : a.shift === 'Day' ? -1 : 1)
    || (a.craft || '').localeCompare(b.craft || '')
    || a.slot_index - b.slot_index);
  const init = {};
  {
    // Saved rows keyed line|shift|index. A slot saved before the position
    // model (or whose primary shift flipped after a phase edit) may sit
    // under the other shift's key — fall back so names don't vanish.
    const raw = {};
    for (const p of (current.personnel || [])) raw[`${p.labor_line_id}|${p.shift}|${p.slot_index}`] = { name: p.name || '', email: p.email || '', phone: p.phone || '' };
    for (const s of slots) {
      const other = s.shift === 'Day' ? 'Night' : 'Day';
      const hit = raw[s.key] || raw[`${s.labor_line_id}|${other}|${s.slot_index}`];
      if (hit) init[s.key] = hit;
    }
  }
  // Subcontractor slots no longer default to a "3rd Party" name: the roster
  // and org chart go to customers, so unnamed slots print TBD server side
  // (utils/personnelSlots.js slotName, George 2026-10-08).
  return { slots, init };
}

export const WD_LABEL = { all: 'all 7 days', mon_fri: 'Mon–Fri', mon_thu: 'Mon–Thu', mon_sat: 'Mon–Sat', rot_13_1: '13 on / 1 off', rot_13_1_stag: '13 on / 1 off, staggered' };
export const mdDate = (d) => { const p = String(d).slice(0, 10).split('-'); return `${Number(p[1])}/${Number(p[2])}/${p[0].slice(2)}`; };

// The card itself. `data` = the GET /workforce/crew-editor payload (parent
// loads it so it can also gate the rest of the drill-in on has_estimate);
// `employees` = the roster the Allocations tab already loads (name
// suggestions + blank email/phone autofill, same UX as PersonnelPanel).
export default function CrewEditorCard({ projectId, data, employees, onSaved, innerRef }) {
  const { slots, init } = useMemo(() => buildCrewSlots(data), [data]);
  const [vals, setVals] = useState(init);
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState('');
  useEffect(() => { setVals(init); setSaveNote(''); }, [init]);

  const dirty = useMemo(() => JSON.stringify(vals) !== JSON.stringify(init), [vals, init]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const rosterByName = useMemo(() => {
    const m = {};
    for (const e of (employees || [])) if (e.full_name) m[e.full_name.trim().toLowerCase()] = e;
    return m;
  }, [employees]);

  const set = (key, field, value) => setVals(prev => ({ ...prev, [key]: { ...(prev[key] || { name: '', email: '', phone: '' }), [field]: value } }));
  // Contact detail belongs to the PERSON, not the slot (George 2026-07-31).
  // Kept identical to PersonnelPanel's setName in Estimating.jsx — the two
  // surfaces write the same rows through the same endpoint.
  const setName = (key, value) => setVals(prev => {
    const cur = prev[key] || { name: '', email: '', phone: '' };
    if (!value.trim()) return { ...prev, [key]: { name: '', email: '', phone: '' } };
    const emp = rosterByName[value.trim().toLowerCase()];
    if (emp) return { ...prev, [key]: { name: value, email: emp.email || '', phone: emp.phone || '' } };
    // Editing away from a known person drops their detail; a hand-typed new
    // hire keeps whatever the user is in the middle of typing.
    const prevWasKnown = !!rosterByName[(cur.name || '').trim().toLowerCase()];
    if (prevWasKnown) return { ...prev, [key]: { name: value, email: '', phone: '' } };
    return { ...prev, [key]: { ...cur, name: value } };
  });

  const save = async () => {
    setSaving(true);
    setSaveNote('');
    try {
      const r = await api.saveCrewEditor(projectId, slots.map(s => ({
        labor_line_id: s.labor_line_id, shift: s.shift, slot_index: s.slot_index,
        name: (vals[s.key] || {}).name || '', email: (vals[s.key] || {}).email || '', phone: (vals[s.key] || {}).phone || '',
      })));
      const sy = r.sync || {};
      setSaveNote(`✓ Saved · assignments synced: +${sy.added || 0} added, ${sy.updated || 0} updated, ${sy.removed || 0} removed`);
      if (onSaved) onSaved();
    } catch (e) {
      alert('Crew save failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const present = [...new Set(slots.map(s => s.section))];
  // Two estimates on the job: sections read "EST-x · Turnaround"; keep the
  // estimates in payload order and the usual section order inside each.
  const estLabels = (data.estimates || []).map(e => e.estimate_label);
  const multiEst = estLabels.length > 1;
  const secRank = (sec) => {
    const i = SECTION_ORDER.indexOf(sec);
    return i < 0 ? SECTION_ORDER.length : i;
  };
  const ordered = multiEst
    ? [...present].sort((a, b) => {
        const [ea, sa] = a.split(' · '), [eb, sb] = b.split(' · ');
        return (estLabels.indexOf(ea) - estLabels.indexOf(eb)) || (secRank(sa) - secRank(sb)) || String(sa).localeCompare(String(sb));
      })
    : [...SECTION_ORDER.filter(s => present.includes(s)), ...present.filter(s => !SECTION_ORDER.includes(s)).sort()];
  const filled = slots.filter(s => { const v = vals[s.key] || {}; return v.name || v.email || v.phone; }).length;

  return (
    <div ref={innerRef} style={{ ...styles.card, borderLeft: '4px solid #1F2937' }}>
      <datalist id="crew-editor-roster-names">
        {(employees || []).filter(e => e.is_active !== false && e.full_name).map(e => <option key={e.id} value={e.full_name} />)}
      </datalist>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
          👷 Crew
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            · from {multiEst ? `estimates ${estLabels.join(' + ')}` : `estimate ${data.estimate_label}`} · {filled} of {slots.length} slots filled
          </span>
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {saveNote && !dirty && <span style={{ fontSize: 12, color: '#065F46' }}>{saveNote}</span>}
          {dirty && <span style={{ fontSize: 12, color: '#92400E', fontWeight: 700 }}>unsaved changes</span>}
          <button style={{ ...(dirty ? styles.btn : styles.btnSecondary), fontSize: 12 }} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save Crew'}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
        Names, craft, and shift are managed here (one list shared with Estimating → Personnel) and sync onto the
        assignments below automatically. Per-person dates, per diem, travel, and verifications stay on the assignment rows.
        Clearing a name removes that person from the job on Save.
      </div>

      {!slots.length && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#6B7280' }}>
          The estimate has no staffed labor yet — headcount on its Labor tab creates the name slots here.
        </div>
      )}

      {ordered.map(section => (
        <div key={section} style={{ marginTop: 12 }}>
          <div style={{ background: '#1F2937', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 4 }}>{section}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...styles.table, fontSize: 12, minWidth: 860 }}>
              <thead><tr>
                <th style={{ ...styles.th, width: 220 }}>Craft / Role</th>
                <th style={{ ...styles.th, width: 60 }}>Shift</th>
                <th style={styles.th}>Schedule (phases)</th>
                <th style={{ ...styles.th, width: 180 }}>Name</th>
                <th style={{ ...styles.th, width: 200 }}>Email</th>
                <th style={{ ...styles.th, width: 130 }}>Phone</th>
              </tr></thead>
              <tbody>
                {slots.filter(s => s.section === section).map(s => {
                  const v = vals[s.key] || { name: '', email: '', phone: '' };
                  return (
                    <tr key={s.key}>
                      <td style={styles.td}>
                        <span style={{ background: '#E0E7FF', color: '#3730A3', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px', marginRight: 6 }}>L{s.line_no}</span>
                        {s.craft}
                        {s.sub && <span style={{ marginLeft: 6, fontSize: 10, color: '#6B7280' }}>(sub)</span>}
                      </td>
                      <td style={styles.td}>{s.shift}{s.rotates && <span title="Rotates shifts across phases — see schedule"> ↔</span>}</td>
                      <td style={{ ...styles.td, fontSize: 11, color: '#374151' }}>
                        {s.phases.length ? s.phases.map((ph, i) => (
                          <div key={i}>
                            <strong>{ph.phase}</strong> {mdDate(ph.start_date)} → {mdDate(ph.end_date)}
                            {ph.hours_per_day ? ` · ${ph.hours_per_day}h/day` : ''}
                            {` · ${WD_LABEL[ph.work_days] || ph.work_days}`}
                            {s.rotates ? ` · ${ph.slotShift}` : ''}
                          </div>
                        )) : <span style={{ color: '#9CA3AF' }}>no dated phases</span>}
                      </td>
                      <td style={styles.td}>
                        <input list="crew-editor-roster-names" style={{ ...styles.input, fontSize: 12 }} value={v.name}
                          placeholder="— open —" onChange={e => setName(s.key, e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input style={{ ...styles.input, fontSize: 12 }} value={v.email} onChange={e => set(s.key, 'email', e.target.value)} />
                      </td>
                      <td style={styles.td}>
                        <input style={{ ...styles.input, fontSize: 12 }} value={v.phone} onChange={e => set(s.key, 'phone', e.target.value)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
